// Date formatting
export const formatDate = (isoString: string | null | undefined): string => {
  // Handle null, undefined, or empty string
  if (!isoString || isoString.trim() === '') {
    return 'N/A';
  }

  const date = new Date(isoString);

  // Check if date is valid (not NaN)
  if (Number.isNaN(date.getTime())) {
    return 'N/A';
  }

  try {
    return new Intl.DateTimeFormat('cs-CZ', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  } catch (error) {
    // Fallback if formatting fails
    console.warn('Date formatting failed:', error, isoString);
    return 'N/A';
  }
};

// File size formatting
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Math.round((bytes / k ** i) * 100) / 100} ${sizes[i]}`;
};

// Extract model ID from preview URL
export const extractModelIdFromUrl = (url: string | null): string | null => {
  if (!url) return null;
  const match = url.match(/\/preview\/(png|gif)\/([a-f0-9-]+)/);
  return match ? match[2] : null;
};

// Format user name from project data
export const formatUserName = (project: {
  // biome-ignore lint/style/useNamingConvention: API uses snake_case
  owner_first_name?: string | null;
  // biome-ignore lint/style/useNamingConvention: API uses snake_case
  owner_last_name?: string | null;
  // biome-ignore lint/style/useNamingConvention: API uses snake_case
  owner_name?: string | null;
  // biome-ignore lint/style/useNamingConvention: API uses snake_case
  owner_id: string;
}): string => {
  // Prefer first_name + last_name
  if (project.owner_first_name || project.owner_last_name) {
    const firstName = project.owner_first_name || '';
    const lastName = project.owner_last_name || '';
    const fullName = `${firstName} ${lastName}`.trim();
    if (fullName) return fullName;
  }

  // Fallback to owner_name if available
  if (project.owner_name) {
    return project.owner_name;
  }

  // Check for other possible name fields in the project object (using type assertion for dynamic access)
  const projectAny = project as Record<string, unknown>;
  const possibleNameFields = [
    'owner_full_name',
    'user_name',
    'user_full_name',
    'user_first_name',
    'user_last_name',
    'name',
    'full_name',
  ];

  for (const field of possibleNameFields) {
    const value = projectAny[field];
    if (value && typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  // Last resort: format owner_id to be more readable (show first 8 chars)
  const ownerId = project.owner_id;
  if (ownerId && ownerId.length > 8) {
    return `${ownerId.substring(0, 8)}...`;
  }

  return ownerId || 'Unknown User';
};

// Date and time formatting (with hours and minutes)
export const formatDateTime = (isoString: string | null | undefined): string => {
  // Handle null, undefined, or empty string
  if (!isoString || isoString.trim() === '') {
    return 'N/A';
  }

  const date = new Date(isoString);

  // Check if date is valid (not NaN)
  if (Number.isNaN(date.getTime())) {
    return 'N/A';
  }

  try {
    return new Intl.DateTimeFormat('cs-CZ', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  } catch (error) {
    // Fallback if formatting fails
    console.warn('DateTime formatting failed:', error, isoString);
    return 'N/A';
  }
};

// Validate .aptsource file
export const isValidAPTSourceFile = (file: File): boolean => {
  const validExtensions = ['.aptsource'];
  const fileName = file.name.toLowerCase();
  return validExtensions.some((ext) => fileName.endsWith(ext));
};

/**
 * Millimetre value for display: at most three decimals, never trailing zeros.
 *
 * Holder dimensions arrive from the CAD import as raw doubles — `63.0000000002226`,
 * `27.3063280567038` — which read as noise in a table. Rounding is display-only; the inline
 * editor still receives the stored value, so nothing is lost by showing `63` and `27.306`.
 *
 * Built by hand rather than with `Intl.NumberFormat` because the table needs a `.` decimal
 * separator regardless of the UI language, and a locale-aware formatter would emit `,` for cs.
 */
export const formatMm = (value: number): string => {
  if (!Number.isFinite(value)) return '—';
  // `toFixed` then trim the tail: unlike `toPrecision` it never reaches exponent notation for
  // the magnitudes a holder dimension can have, and the trim leaves whole numbers bare.
  return value.toFixed(3).replace(/\.?0+$/, '');
};
